import type { SimpleInOutPhoneKind, SimpleInOutUser } from '../../api/simpleInOut';

/**
 * Pure helpers for the Simple I/O dotboard drawer (no React). Rules live in
 * docs/simple-inout-integration.md: present statuses are In, Busy, Remote and
 * Break; a present status unchanged for more than 14 days is flagged as possibly
 * out of date; Out, Sick and Vacation are never flagged.
 */

export type DotboardStatus = 'in' | 'busy' | 'remote' | 'break' | 'out' | 'sick' | 'vacation' | 'unknown';
export type DotboardStatusFilter = 'all' | DotboardStatus;
export type DotboardSort = 'name-asc' | 'name-desc' | 'updated-desc' | 'updated-asc';

export const DOTBOARD_SORT_OPTIONS: Array<{ value: DotboardSort; label: string }> = [
  { value: 'name-asc', label: 'Name A–Z' },
  { value: 'name-desc', label: 'Name Z–A' },
  { value: 'updated-desc', label: 'Updated: newest' },
  { value: 'updated-asc', label: 'Updated: oldest' },
];

export const DOTBOARD_STATUS_ORDER: DotboardStatus[] = ['in', 'busy', 'remote', 'break', 'out', 'sick', 'vacation', 'unknown'];

export const DOTBOARD_STATUS_LABELS: Record<DotboardStatus, string> = {
  in: 'In',
  busy: 'Busy',
  remote: 'Remote',
  break: 'Break',
  out: 'Out',
  sick: 'Sick',
  vacation: 'Vacation',
  unknown: 'Unknown',
};

/** Statuses that only get a chip when someone has them. */
const OPTIONAL_STATUSES: DotboardStatus[] = ['remote', 'busy', 'break', 'sick', 'vacation'];
const PRESENT_STATUSES = new Set<DotboardStatus>(['in', 'busy', 'remote', 'break']);
const KNOWN_STATUSES = new Set<string>(DOTBOARD_STATUS_ORDER);
export const DOTBOARD_STALE_AFTER_MS = 14 * 24 * 60 * 60 * 1000;
const DEFAULT_SIMPLE_INOUT_IMAGE = /image_none\.png(\?|$)/i;
const DAY_MS = 24 * 60 * 60 * 1000;

const PHONE_LABELS: Record<SimpleInOutPhoneKind, string> = {
  mobile: 'Mobile',
  office: 'Office',
  home: 'Home',
  phone: 'Phone',
};

export interface DotboardPhone {
  number: string;
  kind: SimpleInOutPhoneKind;
  label: string;
  href: string;
}

export interface DotboardPerson {
  id: string;
  name: string;
  email: string;
  status: DotboardStatus;
  present: boolean;
  comment: string;
  automatic: boolean;
  /** Milliseconds, or null when Simple I/O sent no time. */
  updatedAt: number | null;
  stale: boolean;
  groupIds: string[];
  phones: DotboardPhone[];
  photo: string | null;
  portalPhoto: string | null;
  jobTitle: string | null;
  profileId: string | null;
  searchText: string;
}

export interface DotboardGroupOption {
  id: string;
  name: string;
  count: number;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function userGroups(user: SimpleInOutUser): Array<{ id: string; name: string }> {
  const groups: Array<{ id: string; name: string }> = [];
  const seen = new Set<string>();
  function add(idValue: unknown, nameValue: unknown) {
    const id = text(idValue);
    if (!id || seen.has(id)) return;
    seen.add(id);
    groups.push({ id, name: text(nameValue) || id });
  }
  for (const membership of user.memberships || []) add(membership.group?.id || membership.group_id, membership.group?.name);
  for (const group of user.groups || []) add(group.id, group.name);
  return groups;
}

/** `tel:` link; an inline extension ("x137") dials after a comma pause. */
export function dotboardTelHref(number: string): string {
  const [main, extension] = number.split(/\s*(?:x|ext\.?|extension)\s*/i);
  const digits = (main || '').replace(/[^\d+]/g, '');
  const ext = (extension || '').replace(/\D/g, '');
  return `tel:${digits}${ext ? `,${ext}` : ''}`;
}

export function toDotboardPeople(users: SimpleInOutUser[], now: number): DotboardPerson[] {
  return users.map((user) => {
    const statusRecord = user.status || user.latest || null;
    const rawStatus = text(statusRecord?.status).toLowerCase();
    const status = (KNOWN_STATUSES.has(rawStatus) ? rawStatus : 'unknown') as DotboardStatus;
    const createdAt = statusRecord?.created_at;
    const updatedAt = typeof createdAt === 'number' && createdAt > 0 ? createdAt * 1000 : null;
    const present = PRESENT_STATUSES.has(status);
    const name = text(user.name) || text(user.email) || user.id;
    const comment = text(statusRecord?.comment);
    const photo = text(user.image_url);
    const groups = userGroups(user);
    return {
      id: user.id,
      name,
      email: text(user.email),
      status,
      present,
      comment,
      automatic: statusRecord?.automatic === true,
      updatedAt,
      stale: present && updatedAt !== null && now - updatedAt > DOTBOARD_STALE_AFTER_MS,
      groupIds: groups.map((group) => group.id),
      phones: (user.phone_numbers || [])
        .filter((phone) => text(phone.number))
        .map((phone) => ({
          number: text(phone.number),
          kind: phone.kind,
          label: PHONE_LABELS[phone.kind] || PHONE_LABELS.phone,
          href: dotboardTelHref(text(phone.number)),
        })),
      photo: photo && !DEFAULT_SIMPLE_INOUT_IMAGE.test(photo) ? photo : null,
      portalPhoto: text(user.portal?.headshot_url) || null,
      jobTitle: text(user.portal?.job_title) || null,
      profileId: text(user.portal?.profile_id) || null,
      searchText: `${name} ${comment}`.toLowerCase(),
    };
  });
}

export function buildDotboardGroups(users: SimpleInOutUser[]): DotboardGroupOption[] {
  const groups = new Map<string, DotboardGroupOption>();
  for (const user of users) {
    for (const group of userGroups(user)) {
      const existing = groups.get(group.id);
      if (existing) existing.count += 1;
      else groups.set(group.id, { id: group.id, name: group.name, count: 1 });
    }
  }
  return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/** Search (name or comment) and group; the status chip is applied afterwards. */
export function filterDotboardPeople(people: DotboardPerson[], query: string, groupId: string): DotboardPerson[] {
  const needle = query.trim().toLowerCase();
  return people.filter((person) => (
    (groupId === 'all' || person.groupIds.includes(groupId))
    && (!needle || person.searchText.includes(needle))
  ));
}

export function sortDotboardPeople(people: DotboardPerson[], sort: DotboardSort): DotboardPerson[] {
  const byName = (a: DotboardPerson, b: DotboardPerson) => a.name.localeCompare(b.name);
  const sorted = people.slice();
  if (sort === 'name-desc') return sorted.sort((a, b) => byName(b, a));
  if (sort === 'updated-desc' || sort === 'updated-asc') {
    const direction = sort === 'updated-desc' ? -1 : 1;
    // People with no time sort last either way.
    return sorted.sort((a, b) => {
      if (a.updatedAt === b.updatedAt) return byName(a, b);
      if (a.updatedAt === null) return 1;
      if (b.updatedAt === null) return -1;
      return (a.updatedAt - b.updatedAt) * direction;
    });
  }
  return sorted.sort(byName);
}

export function isDotboardSort(value: unknown): value is DotboardSort {
  return DOTBOARD_SORT_OPTIONS.some((option) => option.value === value);
}

export function countDotboardStatuses(people: DotboardPerson[]): Map<DotboardStatus, number> {
  const counts = new Map<DotboardStatus, number>();
  for (const person of people) counts.set(person.status, (counts.get(person.status) || 0) + 1);
  return counts;
}

/** In and Out always get a chip; other statuses only when someone has them. */
export function dotboardChipStatuses(counts: Map<DotboardStatus, number>): DotboardStatus[] {
  return DOTBOARD_STATUS_ORDER.filter((status) => (
    status === 'in' || status === 'out' || (counts.get(status) || 0) > 0
  ));
}

/** "No one Remote, Busy, on Break, Sick or on Vacation", or '' when everyone is covered. */
export function dotboardAbsentStatusNote(counts: Map<DotboardStatus, number>): string {
  const phrases: Record<string, string> = {
    remote: 'Remote', busy: 'Busy', break: 'on Break', sick: 'Sick', vacation: 'on Vacation',
  };
  const absent = OPTIONAL_STATUSES.filter((status) => !counts.get(status)).map((status) => phrases[status]);
  if (!absent.length) return '';
  if (absent.length === 1) return `No one ${absent[0]}`;
  return `No one ${absent.slice(0, -1).join(', ')} or ${absent[absent.length - 1]}`;
}

/** "since 6:49 AM" today, "since Sep 2" this year, "since Oct 16, 2024" before. */
export function formatDotboardSince(updatedAt: number | null, now: number): string {
  if (updatedAt === null) return '';
  const date = new Date(updatedAt);
  const today = new Date(now);
  if (date.toDateString() === today.toDateString()) {
    return `since ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  }
  if (date.getFullYear() === today.getFullYear()) {
    return `since ${date.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
  }
  return `since ${date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

/** "3 wk", "5 mo", "2 yr" since the last change. */
export function formatDotboardAge(updatedAt: number | null, now: number): string {
  if (updatedAt === null) return '';
  const days = Math.max(0, Math.floor((now - updatedAt) / DAY_MS));
  if (days < 56) return `${Math.max(1, Math.floor(days / 7))} wk`;
  const months = Math.floor(days / 30.44);
  if (months < 24) return `${months} mo`;
  return `${Math.floor(months / 12)} yr`;
}
